/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect } from 'react';
import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { CloseIcon, ExpandDownIcon } from 'src/icons';
import { CommonElementProps } from '../types';
import ElementUtils from '../utils';

export type MultiSelectChipOption<T, V> = {
  label: T;
  value: V;
};

export interface MultiSelectChipProps<T, V> extends CommonElementProps<V[]> {
  options: MultiSelectChipOption<T, V>[];
  onChange?: (value: V[]) => void;
  onSelectClose?: () => void;
}

const MultiSelectChip = function MultiSelectChip<T, V>(props: MultiSelectChipProps<T, V>) {
  const {
    options,
    onChange,
    placeholder,
    error,
    label,
    disabled,
    onSelectClose,
    value: defaultValue,
  } = props;

  const [value, setValue] = React.useState<V[]>([]);
  const handleChange = (event: SelectChangeEvent<V[]>) => {
    const val = event.target.value as V[];
    setValue(val);
    if (onChange) {
      onChange(val);
    }
  };

  useEffect(() => {
    setValue(defaultValue || []);
  }, [defaultValue]);

  const handleDelete = (e: React.MouseEvent, newValue: MultiSelectChipOption<T, V>) => {
    e.preventDefault();
    const arr = value.filter((item: V) => item !== newValue.value);
    setValue(arr);
    if (onChange) {
      onChange(arr);
    }
  };

  return (
    <FormControl className="w-full flex flex-col gap-1.5 justify-between items-start h-full">
      <InputLabel
        shrink
        color="secondary"
        error={!!error}
        sx={{
          '&.MuiInputLabel-shrink': {
            transform: 'translate(0, 1.5px) scale(1)', // adjust scale as needed
            fontSize: '16px', // adjust font size as needed
          },
        }}
        className="font-bold static leading-tight tracking-wide text-[#28364C] focus:text-[#28364C]"
      >
        {label}
      </InputLabel>
      <Select
        placeholder={placeholder}
        labelId="demo-mutiple-chip-checkbox-label"
        id="demo-mutiple-chip-checkbox"
        className="custom-shadow"
        displayEmpty={!!placeholder}
        fullWidth
        multiple
        onClick={(event) => {
          event.stopPropagation();
        }}
        disabled={disabled}
        error={!!error}
        value={value as any}
        onChange={handleChange}
        sx={{
          '& fieldset': { border: 'none' },
        }}
        classes={{
          error: 'outline outline-[1px] outline-error',
          select: '',
        }}
        onClose={() => onSelectClose && onSelectClose()}
        IconComponent={ExpandDownIcon}
        renderValue={(selected) => {
          if (selected.length === 0 && placeholder) {
            return <em className="text-[#8e8e8e] text-xs">{placeholder}</em>;
          }
          const selectedOptions = options.filter((option: MultiSelectChipOption<T, V>) => (
            selected.includes(option.value as any)
          )) as MultiSelectChipOption<T, V>[];

          const displayedOptions = selectedOptions.slice(0, 2);
          const remainingOptions = selectedOptions.length - 2;

          return (
            <div>
              {displayedOptions.map((currentItem) => (
                <Chip
                  key={currentItem.value as any}
                  label={currentItem.label as any}
                  clickable
                  size="small"
                  deleteIcon={
                    (
                      <div className="flex" onMouseDown={(event) => event.stopPropagation()}>
                        <CloseIcon height={18} width={18} />
                      </div>
                    )
                  }
                  onDelete={(e) => handleDelete(e, currentItem)}
                  onClick={() => console.log('clicked chip')}
                />
              ))}
              {remainingOptions > 0 && (
                <Chip
                  label={`+${remainingOptions}`}
                  size="small"
                />
              )}
            </div>
          );
        }}
      >
        {options.map(({ label: optionLabel, value: newValue }) => (
          <MenuItem key={newValue as any} value={newValue as any}>
            <Checkbox checked={value.includes(newValue)} />
            <ListItemText primary={optionLabel as any} />
          </MenuItem>
        ))}
      </Select>
      {error && (
        <div className="absolute -bottom-5 text-error text-xs">
          {ElementUtils.renderError(error)}
        </div>
      )}
    </FormControl>
  );
};

MultiSelectChip.defaultProps = {
  onChange: () => { },
  onSelectClose: () => { },
};

MultiSelectChip.propTypes = {

};
export default MultiSelectChip;
